import { Component, type ReactNode, type ErrorInfo } from 'react';
import { ErrorLogger, ErrorCategory, ErrorSeverity } from '../../services/errorLogger';
import { StatusWarning as AlertTriangleIcon } from '../icons';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional component name for error context */
  componentName?: string;
  /** Optional callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorContext: {
    component: string;
    timestamp: string;
    userAction?: string;
    route?: string;
  } | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorContext: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Capture error context
    const errorContext = {
      component: this.props.componentName || 'ErrorBoundary',
      timestamp: new Date().toISOString(),
      route: window.location.pathname,
      userAction: this.captureUserAction(),
    };

    // Log error using ErrorLogger service
    ErrorLogger.logError(
      error,
      {
        component: errorContext.component,
        route: errorContext.route,
        action: errorContext.userAction,
        additionalData: {
          componentStack: errorInfo.componentStack,
          errorBoundary: true,
        },
      },
      ErrorCategory.UI,
      ErrorSeverity.ERROR
    );

    // Update state with error details and context
    this.setState({
      error,
      errorInfo,
      errorContext,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Also log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  /**
   * Capture user action from recent events
   * This is a simple implementation that could be enhanced with
   * event tracking or user session recording
   */
  private captureUserAction(): string {
    // Try to get the last focused element or clicked element
    const activeElement = document.activeElement;
    if (activeElement && activeElement !== document.body) {
      const tagName = activeElement.tagName.toLowerCase();
      const id = activeElement.id ? `#${activeElement.id}` : '';
      const className = activeElement.className ? `.${activeElement.className.split(' ')[0]}` : '';
      return `Interacting with ${tagName}${id}${className}`;
    }
    return 'Unknown user action';
  }

  handleReset = (): void => {
    // Log retry attempt
    ErrorLogger.logError(
      'User initiated error recovery',
      {
        component: this.state.errorContext?.component || 'ErrorBoundary',
        action: 'retry',
        route: this.state.errorContext?.route,
        additionalData: {
          originalError: this.state.error?.message,
          timestamp: this.state.errorContext?.timestamp,
        },
      },
      ErrorCategory.UI,
      ErrorSeverity.INFO
    );

    // Reset error state to allow retry
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorContext: null,
    });
  };

  handleReload = (): void => {
    // Log reload attempt
    ErrorLogger.logError(
      'User initiated page reload',
      {
        component: this.state.errorContext?.component || 'ErrorBoundary',
        action: 'reload',
        route: this.state.errorContext?.route,
        additionalData: {
          originalError: this.state.error?.message,
          timestamp: this.state.errorContext?.timestamp,
        },
      },
      ErrorCategory.UI,
      ErrorSeverity.INFO
    );

    window.location.reload();
  };

  /**
   * Get user-friendly error message based on error type
   */
  private getUserFriendlyMessage(): string {
    const error = this.state.error;
    if (!error) {
      return 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.';
    }

    const errorMessage = error.message.toLowerCase();

    // Provide specific messages for known error types
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    if (errorMessage.includes('timeout')) {
      return 'The request took too long to complete. Please try again.';
    }

    if (errorMessage.includes('unauthorized') || errorMessage.includes('authentication')) {
      return 'Your session has expired. Please refresh the page to continue.';
    }

    if (errorMessage.includes('not found') || errorMessage.includes('404')) {
      return 'The requested resource could not be found. Please try refreshing the page.';
    }

    // Default message
    return 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.';
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Render default error UI with enhanced information
      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            <div className="error-boundary__icon"><AlertTriangleIcon size={40} /></div>
            <h1 className="error-boundary__title">Something went wrong</h1>
            <p className="error-boundary__message">
              {this.getUserFriendlyMessage()}
            </p>
            <p className="error-boundary__code">SE-SYS-002</p>
            
            {/* Error context information */}
            {this.state.errorContext && (
              <div className="error-boundary__context">
                <p className="error-boundary__context-item">
                  <strong>When:</strong> {new Date(this.state.errorContext.timestamp).toLocaleString()}
                </p>
                {this.state.errorContext.route && (
                  <p className="error-boundary__context-item">
                    <strong>Where:</strong> {this.state.errorContext.route}
                  </p>
                )}
              </div>
            )}
            
            {/* Expandable error details for debugging */}
            {this.state.error && (
              <details className="error-boundary__details">
                <summary>Technical details</summary>
                <pre className="error-boundary__error-text">
                  <strong>Error:</strong> {this.state.error.toString()}
                  {this.state.error.stack && (
                    <>
                      {'\n\n'}
                      <strong>Stack Trace:</strong>
                      {'\n'}
                      {this.state.error.stack}
                    </>
                  )}
                  {this.state.errorInfo && (
                    <>
                      {'\n\n'}
                      <strong>Component Stack:</strong>
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                  {this.state.errorContext && (
                    <>
                      {'\n\n'}
                      <strong>Context:</strong>
                      {'\n'}
                      {JSON.stringify(this.state.errorContext, null, 2)}
                    </>
                  )}
                </pre>
              </details>
            )}

            {/* Action buttons with retry functionality */}
            <div className="error-boundary__actions">
              <button
                className="error-boundary__button error-boundary__button--primary"
                onClick={this.handleReset}
                type="button"
                aria-label="Try to recover from error"
              >
                Try Again
              </button>
              <button
                className="error-boundary__button error-boundary__button--secondary"
                onClick={this.handleReload}
                type="button"
                aria-label="Reload the page"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    // No error - render children normally
    // This prevents error propagation to parent components
    return this.props.children;
  }
}
